import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Modal, Button, Badge } from '../ui';
import {
	SIZE_OPTIONS,
	LENGTH_OPTIONS,
	formatSize,
	formatLength,
} from '../../constants/serviceOptions';
import type { TaskPricingPayload } from '../../types/appointments';

export interface TaskPricingModalProps {
	isOpen: boolean;
	/** 'edit' keeps size + length fixed (only duration/price change); 'add' collects all four. */
	mode: 'edit' | 'add';
	serviceName?: string;
	/** Task type this row belongs to, e.g. "Front" — shown for context. */
	taskTypeLabel?: string;
	/** Prefilled values when editing an existing row. */
	initial?: TaskPricingPayload | null;
	isSaving: boolean;
	onClose: () => void;
	onSubmit: (data: TaskPricingPayload) => void;
}

const TaskPricingModal = ({
	isOpen,
	mode,
	serviceName,
	taskTypeLabel,
	initial,
	isSaving,
	onClose,
	onSubmit,
}: TaskPricingModalProps) => {
	const isEdit = mode === 'edit';

	const [size, setSize] = useState('');
	const [length, setLength] = useState('');
	const [duration, setDuration] = useState('');
	const [price, setPrice] = useState('');

	useEffect(() => {
		if (isOpen) {
			setSize(initial?.size ?? '');
			setLength(initial?.length ?? '');
			setDuration(initial ? String(initial.durationMinutes) : '');
			setPrice(initial ? String(initial.price) : '');
		}
	}, [isOpen, initial]);

	const handleSubmit = () => {
		if (!isEdit && (!size || !length)) {
			toast.error('Please select both size and length');
			return;
		}

		const durationMinutes = parseInt(duration) || 0;
		const priceValue = parseFloat(price) || 0;

		if (durationMinutes <= 0) {
			toast.error('Please enter a valid duration');
			return;
		}
		if (priceValue <= 0) {
			toast.error('Please enter a valid price');
			return;
		}

		// Uniqueness of size+length is enforced by the backend, which is the only
		// source of truth for what rows currently exist. A client-side guard here
		// would block on stale/other-task-type rows and can't tell "already saved"
		// from "should have been deleted".
		onSubmit({ size, length, durationMinutes, price: priceValue });
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEdit ? 'Edit Pricing' : 'Add Size'}
			size="md"
		>
			<div className="space-y-4">
				{(serviceName || taskTypeLabel) && (
					<p className="text-sm text-gray-500 dark:text-gray-400">
						{[serviceName, taskTypeLabel].filter(Boolean).join(' · ')}
					</p>
				)}

				<div className="grid grid-cols-2 gap-4">
					<div className="provider-form__group">
						<label className="provider-form__label">
							Size
							{!isEdit && <span className="provider-form__required">*</span>}
						</label>
						{isEdit ? (
							<div className="pt-1">
								<Badge variant="primary">{formatSize(size)}</Badge>
							</div>
						) : (
							<select
								className="provider-form__input"
								value={size}
								onChange={e => setSize(e.target.value)}
							>
								<option value="">Select</option>
								{SIZE_OPTIONS.map(option => (
									<option key={option} value={option}>
										{formatSize(option)}
									</option>
								))}
							</select>
						)}
					</div>

					<div className="provider-form__group">
						<label className="provider-form__label">
							Length
							{!isEdit && <span className="provider-form__required">*</span>}
						</label>
						{isEdit ? (
							<div className="pt-1">
								<Badge variant="info">{formatLength(length)}</Badge>
							</div>
						) : (
							<select
								className="provider-form__input"
								value={length}
								onChange={e => setLength(e.target.value)}
							>
								<option value="">Select</option>
								{LENGTH_OPTIONS.map(option => (
									<option key={option} value={option}>
										{formatLength(option)}
									</option>
								))}
							</select>
						)}
					</div>

					<div className="provider-form__group">
						<label className="provider-form__label">
							Duration (min)<span className="provider-form__required">*</span>
						</label>
						<input
							type="number"
							className="provider-form__input"
							placeholder="180"
							min={5}
							step={5}
							value={duration}
							onChange={e => setDuration(e.target.value)}
						/>
					</div>

					<div className="provider-form__group">
						<label className="provider-form__label">
							Price ($)<span className="provider-form__required">*</span>
						</label>
						<input
							type="number"
							className="provider-form__input"
							placeholder="0"
							min={0}
							step={0.01}
							value={price}
							onChange={e => setPrice(e.target.value)}
						/>
					</div>
				</div>

				{isEdit && (
					<p className="text-xs text-gray-400 dark:text-gray-500">
						Size and length identify this row and can&apos;t be changed here. To use a
						different size or length, delete this row and add a new one.
					</p>
				)}

				<div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
					<Button variant="secondary" onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSubmit} isLoading={isSaving}>
						{isEdit ? 'Save' : 'Add Size'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default TaskPricingModal;
